/**
 * Handler for keyboard shortcuts in Sparx Web
 *
 * Views and components can add/remove keyboard shortcuts - i.e. functions to
 * call when certain keys are pressed. The keyboard handler ensures that
 * shortcuts do not activate when an alert is open (unless they are
 * shortcuts within the alert!) and also reports an error to the console if
 * more than one shortcut is ever fired for the same keypress (indicating a
 * clashing shortcut key to the developer)
 */

import log from 'loglevel';

import { getKeyPressed } from 'phoenix2/utils/keyboard';
import { getWaitVisible } from 'phoenix2/state/wait/selectors';

const DEFAULT_HANDLER_ID = 'default';

const ELEMENT_BLACKLIST = ['INPUT', 'TEXTAREA', 'SELECT', 'OPTION'];

/**
 * Keyboard handlers. In the normal Sparx Web app, there is just a single
 * handler with id "default". In unit tests run in parallel each test will
 * have its own keyboard handler with a sequential id
 */
const handlers = {};

let idCounter = 0;

/*
    The order by which keyboard inputs should trigger actions, based on what's
    on screen. When a keyboard event occurs, only the modal earliest in the
    array will be affected. (e.g. if an alert and a tutorial are on screen
    at the same time)
 */
const MODAL_PRIORITY_LIST = [
    ['alert', 'video alert'],
    ['image-zoom', 'zoom-overlay'],
    ['wac-overlay'],
    ['tutorial bubble'],
    ['expression-keypad'],
];

/*
    For each kind of modal, if "true" then ANY keypress on a lower-priority
    modal should be disregarded, even if it does not affect this modal
 */
const BLOCKS_LOWER_INTERACTION = {
    alert: true,
    'video alert': true,
    'wac-overlay': true,
    'image-zoom': true,
    'zoom-overlay': true,
    'tutorial bubble': false,
    'expression-keypad': true,
};

// For a given keyboard shortcut, determine what priority it should be given
const priorityOfShortcut = shortcut => {
    for (const prioritySet of MODAL_PRIORITY_LIST) {
        if (prioritySet.indexOf(shortcut.originator) !== -1) {
            const index = MODAL_PRIORITY_LIST.indexOf(prioritySet);
            return MODAL_PRIORITY_LIST.length - index;
        }
    }
    return 0;
};

export class KeyboardShortcutHandler {
    constructor(store, rootElement = document) {
        this.store = store;

        this.shortcuts = {};

        // Add a listener for key press events
        this._eventListener = this._handleKeypress.bind(this);
        rootElement.addEventListener('keydown', this._eventListener);

        log.debug('Keyboard shortcut handler initialised', store);
    }

    /**
     * Adds a set of keyboard shortcuts
     * @param shortcuts an object where the keys are the keyboard keys that
     * activates the shortcut and their values are argumentless function to
     * be called when each key is pressed by the user. The functions can
     * optionally return true or false to signify whether the action
     * actually had an effect. (If the action had no effect, it is not used
     * when determining if there are shortcut clashes to report as a console
     * error)
     * @param originator a string identifying which view or component has
     * provided the shortcuts (just used for logging messages)
     * @returns {number} an id for this set of shortcuts which can be used to
     * remove them when no longer needed
     */
    addKeyboardShortcuts(shortcuts, originator) {
        this.shortcuts[idCounter] = {
            originator,
            shortcuts,
        };

        // Return id and increment
        return idCounter++;
    }

    /**
     * Removes the set of keyboard shortcuts with the given id
     * @param id the id provided when the shortcuts were added
     */
    removeKeyboardShortcuts(id) {
        delete this.shortcuts[id];
    }

    /**
     * Handler for when the user presses a key - fires the action for any
     * shortcuts associated with the key (unless an alert is showing and the
     * shortcut does not activate when alerts are showing)
     * @param evt
     */
    _handleKeypress(evt) {
        // if a spinner is showing, do not do anything
        if (getWaitVisible(this.store.getState())) {
            return;
        }

        // Ignore keypresses that come from any DOM element you might type into
        if (evt.target && ELEMENT_BLACKLIST.indexOf(evt.target.nodeName) !== -1) {
            return;
        }

        const keyPressed = getKeyPressed(evt);

        if (keyPressed) {
            const shortcutsFound = [];

            // Iterate over all shortcuts that respond to this key.
            // or is present and will
            // Only shortcuts of this priority will be triggered.
            let maximumModalPriority = 0;
            for (const prop in this.shortcuts) {
                if (Object.prototype.hasOwnProperty.call(this.shortcuts, prop)) {
                    const s = this.shortcuts[prop];

                    // If an originator is such that it should block ANY lower-
                    // priority shortcut from being triggered, regardless of
                    // key, include it when caluclating maximum priority
                    const shouldBlockAnyKey = BLOCKS_LOWER_INTERACTION[s.originator];

                    if (s.shortcuts[keyPressed] || shouldBlockAnyKey) {
                        const priority = priorityOfShortcut(s);
                        if (priority > maximumModalPriority) {
                            maximumModalPriority = priority;
                        }
                    }
                }
            }

            // Iterate through all the shortcuts, and fire their action if
            // any are applicable for the key pressed. If there is more
            // than one binding for a key, raise an error to Javascript
            // console - this means the same key has been chosen for two
            // different shortcuts
            for (const prop in this.shortcuts) {
                if (Object.prototype.hasOwnProperty.call(this.shortcuts, prop)) {
                    const s = this.shortcuts[prop];
                    const action = s.shortcuts[keyPressed];

                    // determine if this shortcut can be activated or if it
                    // is blocked by higher-priority modals (e.g. alerts)
                    const priority = priorityOfShortcut(s);
                    const priorityIsValid = priority >= maximumModalPriority;

                    if (action && priorityIsValid) {
                        const hadEffect = action(evt);

                        log.debug(
                            'Keyboard shortcut activated - key: "' +
                                keyPressed +
                                '", originator: ' +
                                s.originator,
                        );

                        if (hadEffect === undefined || hadEffect) {
                            shortcutsFound.push(s.originator);
                        }
                    }
                }
            }

            // If two shortcuts fired for the key, log that there was a clash!
            if (shortcutsFound.length > 1) {
                log.error(
                    'Clashing keyboard shortcuts for key "' +
                        keyPressed +
                        '" from originators: ' +
                        shortcutsFound.join(', '),
                );
            }
        }
    }

    /**
     * Clean up the handler, removing the window event listener. Needed in
     * unit tests where we might create multiple apps in the same JavaScript
     * process.
     */
    dispose() {
        if (this._eventListener) {
            window.removeEventListener('keydown', this._eventListener);
            this.shortcuts = {};
        }
    }
}

/**
 * Initialise the keyboard shortcut hander for the given Redux store. If a
 * handler has already been supplied with the same id, replace it
 * @param store the Redux store - this is needed to determine whether alerts
 * are showing when keys are pressed
 * @param the root element to attach keydown listeners to
 * @param id an id for the handler (allows different unique handlers for
 * tests run in parallel)
 */
export const initKeyboardShortcutHandler = (store, rootElement, id = null) => {
    const handlerId = id || DEFAULT_HANDLER_ID;

    // Clean up previous handler if there was one:
    if (handlers[handlerId]) {
        throw 'Tried to add multiple keyboard handler for app - id: ' + id;
    }
    const handler = new KeyboardShortcutHandler(store, rootElement);
    handlers[handlerId] = handler;
};

/**
 * Adds a set of keyboard shortcuts
 * @param shortcuts an object where the keys are the keyboard keys that
 * activates the shortcut and their values are argumentless function to
 * be called when each key is pressed by the user.  The functions can
 * optionally return true or false to signify whether the action
 * actually had an effect. (If the action had no effect, it is not used
 * when determining if there are shortcut clashes to report as a console
 * error)
 * @param originator a string identifying which view or component has
 * provided the shortcuts (just used for logging messages)
 * @param handlerId the id of the handler to use, if null uses the default
 * handler
 * @returns {number} an id for this set of shortcuts which can be used to
 * remove them when no longer needed
 */
export const addKeyboardShortcuts = (bindings, originator, handlerId = null) => {
    const id = handlerId || DEFAULT_HANDLER_ID;

    if (handlers[id]) {
        return handlers[id].addKeyboardShortcuts(bindings, originator);
    }
};

/**
 * Removes the set of keyboard shortcuts with the given id
 * @param id the id provided when the shortcuts were added
 * @param handlerId the id of the keyboard handler. If null, uses the
 * default handler
 */
export const removeKeyboardShortcuts = (id, handlerId = null) => {
    const keyboardHandlerId = handlerId || DEFAULT_HANDLER_ID;

    if (handlers[keyboardHandlerId]) {
        handlers[keyboardHandlerId].removeKeyboardShortcuts(id);
    }
};
